import { STAGING_MEDIA_BUILD } from '@/config/env';
import dayLg from '@/assets/owner/storefront-day-1600.webp';
import dayMd from '@/assets/owner/storefront-day-1024.webp';
import daySm from '@/assets/owner/storefront-day-640.webp';
import nightLg from '@/assets/owner/storefront-night-1600.webp';
import nightMd from '@/assets/owner/storefront-night-1024.webp';
import nightSm from '@/assets/owner/storefront-night-640.webp';

/**
 * Real photography registry (owner instruction 2026-09-25: no AI imagery,
 * every visual with provenance). Classifications match docs/real-asset-audit.md.
 *
 * - REAL_OWNER_PROVIDED photographs supplied directly by the owner are
 *   bundled and may ship in every build (licence plates blurred).
 * - Owner photographs published on the owner's own prototype
 *   (tint-trace-flow.lovable.app) are referenced by URL in NON-PRODUCTION
 *   builds only: they could not be downloaded or visually inspected from the
 *   build environment, so they are compiled out of production until the
 *   owner re-hosts and approves them (media_assets, approval_status = approved).
 */

export type AssetClass =
  | 'REAL_OWNER_PROVIDED'
  | 'REAL_LICENSED'
  | 'OEM_APPROVED'
  | 'PROTOTYPE_REFERENCE'
  | 'DEVELOPMENT_ONLY'
  | 'MISSING';

export interface Photo {
  src: string;
  srcSet?: string;
  /** Intrinsic size when known (bundled files). Omitted for hosted photos whose size is not recorded, so they render at their own aspect ratio. */
  width?: number;
  height?: number;
  altEn: string;
  altAr: string;
  classification: AssetClass;
  source: string;
  /** Set when the image is served from its original host rather than bundled. */
  external?: boolean;
  notes?: string;
}

export const STOREFRONT_NIGHT: Photo = {
  src: nightLg,
  srcSet: `${nightSm} 640w, ${nightMd} 1024w, ${nightLg} 1600w`,
  width: 1600,
  height: 1200,
  altEn: 'An Al-Qinaa Johnson Window Films branch at night — lit showroom and installation bays',
  altAr: 'أحد فروع جونسون القناع الشفاف ليلًا — صالة العرض ومنطقة التركيب مضاءتان',
  classification: 'REAL_OWNER_PROVIDED',
  source: 'Owner-supplied photograph, 2026-09-25',
};

export const STOREFRONT_DAY: Photo = {
  src: dayLg,
  srcSet: `${daySm} 640w, ${dayMd} 1024w, ${dayLg} 1600w`,
  width: 1600,
  height: 1200,
  altEn: 'An Al-Qinaa Johnson Window Films branch in daylight',
  altAr: 'أحد فروع جونسون القناع الشفاف في النهار',
  classification: 'REAL_OWNER_PROVIDED',
  source: 'Owner-supplied photograph, 2026-09-25',
  notes: 'Licence plates of parked vehicles blurred before publication.',
};

const PROTOTYPE_HOST = 'https://tint-trace-flow.lovable.app/__l5e/assets-v1';

function prototypePhoto(path: string, altEn: string, altAr: string): Photo {
  return {
    src: `${PROTOTYPE_HOST}/${path}`,
    altEn,
    altAr,
    classification: 'REAL_OWNER_PROVIDED',
    source: 'Owner photograph published on the owner’s prototype (tint-trace-flow.lovable.app), owner-authorized 2026-09-25',
    external: true,
    notes: 'Not re-hosted from the build environment; staging only until re-hosted and approved. Shown whole, at its own aspect ratio.',
  };
}

/**
 * Staging-only owner photographs; `null` in production builds (compiled out).
 *
 * Only the two images the owner named in the correction pass (2026-09-25)
 * are used: Ahmed's historical photograph and the authenticity / packaging
 * image. The other images of that prototype set (workshop, showroom,
 * showroom reception, paint-protection section, campaign material) are
 * WITHHELD from every customer page: the one that could be inspected (the
 * workshop) appears computer-generated, with generic vehicles, so none of
 * that set is shown until the owner confirms each is a real photograph
 * (docs/real-asset-audit.md).
 */
export const PROTOTYPE_PHOTOS = STAGING_MEDIA_BUILD
  ? {
      ahmedHistoric: prototypePhoto(
        '8d0a8ea7-04c1-413c-81b6-bfec04e28f9c/PHOTO-2026-08-15-02-34-04.jpg',
        'Historical photograph: Ahmed Al-Sharif Al-Husseini installing window film on a car himself',
        'صورة تاريخية: أحمد الشريف الحسيني أثناء تركيب فيلم لسيارة بنفسه',
      ),
      productComponents: prototypePhoto(
        'fedc720e-2651-45c9-b437-164d35f96b59/PHOTO-2026-08-15-02-24-23_2.jpg',
        'The components of an original Johnson film product',
        'مكونات المنتج الأصلي من أفلام جونسون',
      ),
    }
  : null;

export type PrototypePhotoKey = keyof NonNullable<typeof PROTOTYPE_PHOTOS>;
